import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';

export type DealerListKind = 'authorized_list' | 'locator' | 'letter_only';

export interface DealerLocatorClaimStore {
  organizationId: string;
  organizationName: string;
  status: 'unverified' | 'pending' | 'verified';
  staleListMatch?: {
    confidence: string;
    dealerName: string | null;
    listAge: number;
  } | null;
}

export interface DealerLocatorSourceRow {
  brandId: string;
  brandName: string;
  method: string;
  listKind: DealerListKind;
  publicUrl: string;
  sourceLabel: string;
  notes: string;
  claimTime: boolean;
  needsCoordinates: boolean;
  claims?: { unverified: number; pending: number; verified: number };
  stores?: DealerLocatorClaimStore[];
}

export interface LocatorLookupResult {
  brandId: string;
  method: string;
  matched: boolean;
  /** Present on live lookups. A failed fetch is `unavailable`, not a miss. */
  attempt?: 'matched' | 'miss' | 'unavailable' | 'not_configured';
  candidate?: { name: string; street?: string; city?: string; state?: string; zip?: string };
  candidateCount: number;
  notes: string;
}

export interface CheckStoresResult {
  brandId: string;
  checked: number;
  verified: number;
  /** Set when the published list could not be fetched. Not a per-store miss. */
  listFetchError?: string | null;
  results: Array<{
    organizationId: string;
    organizationName: string;
    matched: boolean;
    notes: string;
    status: string;
    outcome?: 'verified' | 'miss' | 'unavailable' | 'partial' | 'stale' | 'skipped';
    partialMatch?: { dealerName: string; city?: string | null; state?: string | null } | null;
  }>;
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await makeApiCall(endpoint, {
    ...options,
    headers: {
      ...(await getAuthHeaders()),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error?.message || payload.message || `HTTP ${response.status}`);
  }
  return payload.data as T;
}

export const dealerLocatorApi = {
  list: () => request<DealerLocatorSourceRow[]>('/admin/dealer-locators'),
  probe: (brandId: string) =>
    request<{ source: DealerLocatorSourceRow; result: LocatorLookupResult; fixture: string }>(
      `/admin/dealer-locators/${encodeURIComponent(brandId)}/probe`,
      { method: 'POST' },
    ),
  checkStores: (brandId: string) =>
    request<CheckStoresResult>(`/admin/dealer-locators/${encodeURIComponent(brandId)}/check-stores`, {
      method: 'POST',
    }),
  approvePartialMatch: (brandId: string, organizationId: string, notes: string) =>
    request<unknown>(
      `/admin/dealer-locators/${encodeURIComponent(brandId)}/approve-partial-match/${encodeURIComponent(organizationId)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      },
    ),
  approveStaleList: (brandId: string, organizationId: string) =>
    request<unknown>(
      `/admin/dealer-locators/${encodeURIComponent(brandId)}/approve-stale-list/${encodeURIComponent(organizationId)}`,
      { method: 'POST' },
    ),
};
